import IconButton from 'components/IconButton';
import { motion } from 'framer-motion';
import { Dispatch, FC, SetStateAction, useMemo } from 'react';
import { useEffect, useRef, useState } from 'react';

interface BeamTrackerProps {
  currentPage: number;
  totalPage: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  onChangePageHistory: (page: number) => void;
}

const BeamTracker: FC<BeamTrackerProps> = ({
  currentPage,
  setCurrentPage,
  onChangePageHistory,
  totalPage,
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [trackWidth, setTrackWidth] = useState(0);

  useEffect(() => {
    if (trackRef.current) {
      setTrackWidth(trackRef.current.offsetWidth);
    }
  }, []);

  const segmentWidth = useMemo(
    () => trackWidth / totalPage,
    [totalPage, trackWidth],
  );
  const xPosition = useMemo(
    () => currentPage * segmentWidth,
    [currentPage, segmentWidth],
  );

  return (
    <div className="group relative mt-3.5">
      <div ref={trackRef} className="flex h-[13px] items-center">
        <div className="relative h-[3px] w-full overflow-hidden bg-neutral-700/50">
          {trackWidth > 0 && (
            <motion.div
              className="h-full bg-[#6C9DFF]"
              initial={false}
              animate={{ x: xPosition }}
              transition={{ duration: 0.3 }}
              style={{
                width: `${segmentWidth}px`,
                position: 'absolute',
                top: 0,
                left: 0,
              }}
            />
          )}
        </div>
      </div>

      {totalPage > 1 && (
        <motion.div
          className="absolute right-0 top-1/2 hidden -translate-y-1/2 gap-2 bg-blue-900 group-hover:flex"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{
            type: 'spring',
            mass: 1,
            stiffness: 256,
            damping: 24,
          }}
        >
          <IconButton
            icon="chevronBack"
            buttonType="secondary"
            onClick={() =>
              setCurrentPage((prev) => {
                const newPage = Math.max(0, prev - 1);
                onChangePageHistory(newPage);
                return newPage;
              })
            }
          />
          <IconButton
            icon="chevronForward"
            buttonType="secondary"
            onClick={() =>
              setCurrentPage((prev) => {
                const newPage = Math.min(totalPage - 1, prev + 1);
                onChangePageHistory(newPage);
                return newPage;
              })
            }
          />
        </motion.div>
      )}
    </div>
  );
};

export default BeamTracker;
